import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { safelySetInnerHTML } from '@udemy-v2/utils';
import { CoursePrice } from '@udemy-v2/react-course-pricing';
import { SubscriptionPlanContext, ConsumerSubscriptionPlan } from '@udemy-v2/subscription-graphql';
import { REVIEW_MAX_RATING, REVIEW_MIN_RATING } from '@/features/course-landing-page/components/rating-display/constants';
import { getDifficultyLevel } from '@/app/lib/course/get-difficulty-level';
import styles from './course-seo.module.scss';
import { Course, CourseCategory } from '@udemy-v2/browse-course';
export function getDurationData(durationInSeconds: number | undefined) {
  if (!durationInSeconds) {
    return undefined;
  }
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor(durationInSeconds % 3600 / 60);
  let duration = 'PT';
  if (hours > 0) {
    duration += `${hours}H`;
  }
  if (minutes > 0) {
    duration += `${minutes}M`;
  }
  return duration === 'PT' ? undefined : duration;
}
export function getBreadcrumbs(course: Course) {
  const breadcrumbs = (course.categories ?? []).map((category: CourseCategory, index: number) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: category.name,
    item: `https://www.udemy.com${category.url}`
  }));
  if (breadcrumbs.length === 0) {
    return null;
  }
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs
  };
}
export enum OfferCategory {
  PAID = 'Paid',
  SUBSCRIPTION = 'Subscription',
  FREE = 'Free',
}
interface Offer {
  '@type': 'Offer';
  category: OfferCategory;
  price?: number;
  priceCurrency?: string;
}
export interface GetCourseOffersParams {
  course: Course;
  coursePrice?: CoursePrice;
  subscriptionContext?: SubscriptionPlanContext<ConsumerSubscriptionPlan>;
}

/**
 * Generate course offers for SEO schema (server-side compatible)
 * Note: Subscription offers are now handled client-side in CourseSeoPricing
 */
export function getCourseOffers({
  course,
  coursePrice,
  subscriptionContext
}: GetCourseOffersParams): Offer[] {
  const offers: Offer[] = [];
  if (course.isFree) {
    offers.push({
      '@type': 'Offer',
      category: OfferCategory.FREE
    });
    return offers;
  }
  if (coursePrice) {
    offers.push({
      '@type': 'Offer',
      category: OfferCategory.PAID,
      price: coursePrice.price.amount,
      priceCurrency: coursePrice.price.currency
    });
  }

  // Subscription offers are handled client-side via SubscriptionContext
  // Currently hardcoded to undefined - will be implemented later
  if (subscriptionContext) {
    offers.push({
      '@type': 'Offer',
      category: OfferCategory.SUBSCRIPTION
    });
  }
  return offers;
}
export function getSyllabusSections(course: Course) {
  return course.curriculum?.sections.slice(0, 50).map(section => ({
    '@type': 'Syllabus',
    name: section.title,
    timeRequired: getDurationData(section.contentLength)
  })) ?? [];
}
export function getCourseAboutInfo(course: Course) {
  const primaryCategory = course.categories?.[0]?.name;
  return primaryCategory ? [primaryCategory] : [];
}

//const daysAgo = (days: number) => Date.now() - days * 24 * 60 * 60 * 1000;

/**
 * Generates the base course schema without pricing data
 * This can be rendered server-side for ISR compatibility
 */
export function generateCourseSchema(course: Course, coursePrice?: CoursePrice) {
  const instructors = course.instructors.map(instructor => ({
    '@type': 'Person',
    name: instructor.name || ''
  })).filter(i => i.name) ?? [];
  const firstInstructor = instructors[0];
  const provider = firstInstructor ? {
    provider: {
      '@type': 'Organization',
      name: firstInstructor.name,
      sameAs: `https://www.udemy.com/user/${encodeURIComponent(firstInstructor.name)}/`
    }
  } : {};
  const courseSchema = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    '@id': course.urlCourseLanding,
    about: {
      '@type': 'Thing',
      name: getCourseAboutInfo(course)
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: course.rating.average?.toFixed(1),
      ratingCount: course.rating.count,
      bestRating: REVIEW_MAX_RATING,
      worstRating: REVIEW_MIN_RATING,
      reviewCount: course.rating.count
    },
    audience: {
      '@type': 'Audience',
      audienceType: course.targetAudience
    },
    author: instructors,
    datePublished: course.updatedOn,
    description: course.headline || course.description,
    educationalCredentialAwarded: {
      '@type': 'EducationalOccupationalCredential',
      name: 'Udemy Certificate',
      credentialCategory: 'Certificate',
      url: 'https://support.udemy.com/hc/en-us/sections/360011037194-Certificates-of-Completion?type=student'
    },
    educationalLevel: getDifficultyLevel(course.level),
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'Online',
      courseWorkload: getDurationData(course.durationInSeconds),
      instructor: instructors
    },
    image: course.images.px480x270,
    inLanguage: course.locale,
    isAccessibleForFree: course.isFree,
    name: course.title,
    offers: getCourseOffers({
      course,
      coursePrice,
      subscriptionContext: undefined // Hardcoded to undefined for now
    }),
    ...provider,
    publisher: {
      '@type': 'Organization',
      name: 'Udemy',
      sameAs: 'https://en.wikipedia.org/wiki/Udemy',
      url: 'https://www.udemy.com'
    },
    syllabusSections: getSyllabusSections(course),
    availableLanguage: course.captions?.map(caption => `${caption.locale} ${caption.isAutoGenerated ? '[Auto]' : '[CC]'}`),
    teaches: course.learningOutcomes.slice(0, 8),
    url: course.urlCourseLanding
  };
  const breadcrumbSchema = getBreadcrumbs(course);
  return {
    '@context': 'https://schema.org',
    '@graph': [courseSchema, breadcrumbSchema].filter(Boolean)
  };
}
export interface CourseSeoProps {
  course: Course;
}

/**
 * Server-side component that renders SEO schema without pricing data
 * This is ISR-compatible as it doesn't use any hooks
 */
export function CourseSeoServer(t0) {
  const $ = _c(17);
  const {
    course
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== course) {
    const schema = generateCourseSchema(course);
    const schemaString = JSON.stringify(schema);
    t3 = "schema_markup";
    t4 = "schema_markup";
    t1 = "application/ld+json";
    t2 = safelySetInnerHTML({
      descriptionOfCaller: "course-landing-page/seo-info",
      html: schemaString
    });
    $[0] = course;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t2) {
    t5 = <script type={t1} {...t2} />;
    $[5] = t1;
    $[6] = t2;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t6 = <div id={t3} data-purpose={t4}>{t5}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const schemaMarkup = t6;
  let t7;
  if ($[12] !== course) {
    t7 = true ? null : <div className="seo-info" itemScope={true} itemType="http://schema.org/CreativeWork"><span className={styles.hidden} itemProp="dateModified">{course.updatedOn}</span></div>;
    $[12] = course;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const modifiedDate = t7;
  let t8;
  if ($[14] !== modifiedDate || $[15] !== schemaMarkup) {
    t8 = <>{modifiedDate}{schemaMarkup}</>;
    $[14] = modifiedDate;
    $[15] = schemaMarkup;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}

/**
 * Note: CourseSeo wrapper component removed for ISR compatibility.
 * Components now rendered separately:
 * - CourseSeoServer: Rendered at page level for ISR compatibility
 * - CourseSeoPricing: Rendered in client-side provider tree for hook access
 */